// © Microsoft Corporation. All rights reserved.

import "../publicPath";

import { DarkModeBackgroundColor, ExperienceInitialProps } from "@msnews/experiences-base";
import { RequestContext, TelemetryBase, UrlUtility, appEnvironment, resolver } from "@msnews/core";
import { SocialBarConnector, SocialBarReducer, ToolingInfo as SocialBarToolingInfo } from "@msnews/social-bar";
import { SocialCommentConnector, SocialCommentReducer, ToolingInfo as SocialCommentToolingInfo } from "@msnews/social-comment";
import { SocialConnector, SocialReducer, ToolingInfo as SocialToolingInfo } from "@msnews/social";
import { SocialDataConnector, SocialDataReducer } from "@msnews/social-data-connector";
import { handleAppErrorEventCall, handlePartialPageImpressionCall, handleSetTrackingAttrs, handleTelemetryCall } from "../integration/PrimeTelemetry";

import { ConnectorNamespaces } from "@msnews/experiences-constants";
import { DesignSystemDefaults } from "@microsoft/fast-components-styles-msft";
import { DesignSystemProvider } from "@microsoft/fast-jss-manager-react";
import { Direction } from "@microsoft/fast-web-utilities";
import { IExperienceConfig } from "cms-models-msn/configuration/IExperienceConfig";
import { Mediator } from "../integration/Mediator";
import { PageBase } from "@msnews/experiences-page-base";
import React from "react";
import { sendRequest } from "@msnews/oneservice";

// Containers for experiences
const roots: { [id: string]: HTMLElement; } = {};
const apps: { [id: string]: PageBase; } = {};
let experienceConfigSocial: IExperienceConfig = null;
let experienceConfigSocialBar: IExperienceConfig = null;
let experienceConfigSocialComment: IExperienceConfig = null;

// Map base telemetry to prime library for hybrid
TelemetryBase.trackAppErrorEvent = handleAppErrorEventCall;
TelemetryBase.trackPartialImpression = handlePartialPageImpressionCall;
TelemetryBase.trackEvent = handleTelemetryCall;
TelemetryBase.setTrackingAttrs = handleSetTrackingAttrs;

function getFlight(flightId): boolean {
    if (appEnvironment.CurrentRequestTargetScope && appEnvironment.CurrentRequestTargetScope.pageExperiments && flightId) {
        const experiments = appEnvironment.CurrentRequestTargetScope.pageExperiments;
        return !!experiments.find(x => x && x.toLowerCase() == flightId.toLowerCase());
    }
    return false;
}

function getPageType() {
    const href = document.location && document.location.href;
    if (!href) {
        return undefined;
    }
    try {
        const market = href.split("/").find(x => x.length === 5 && x[2] == "-").toLowerCase();
        const typeAndId = href.split("?")[0].split("#")[0].split("/").pop();
        const pageType = typeAndId.indexOf("ar-") === 0 && "article" || typeAndId.indexOf("ss-") === 0 && "slideshow" || typeAndId.indexOf("vi-") === 0 && "video";
        if (pageType && market) {
            const id = typeAndId.split("-").pop();
            return {
                pageType,
                id,
                market
            };
        }
    } catch (e) {
        // no-op
    }
    return undefined;
}

function addSocialBarIntoArticle(id, market) {
    try {
        // Todo make sure it works with embedded articles
        const container = document.getElementsByClassName("authorinfo-flexar")[0].parentElement;
        const newContainer = document.createElement("div");
        newContainer.className = "authorinfo-flexar";
        newContainer.style.display = "block";
        newContainer.innerHTML = "<div id=\"social-experience-socialbar-fixed\" data-social-module=\"socialBar\"></div>";
        const exp = newContainer.children[0] as HTMLDivElement;
        exp.setAttribute("data-social-id", id);
        exp.setAttribute("data-social-market", market);
        const separator = container.getElementsByTagName("hr")[0];
        if (separator) {
            container.insertBefore(newContainer, separator);
        } else {
            container.appendChild(newContainer);
        }
    } catch (e) {
        // no-op
    }
}

// Add social bar container into video page
function addSocialBarIntoVideo(id, market) {
    try {
        // Todo make sure it works with embedded videos
        const container = document.getElementsByClassName("metadata")[0].parentElement;
        const newContainer = document.createElement("div");
        newContainer.className = "metadata";
        newContainer.innerHTML = "<div id=\"social-experience-socialbar-fixed\" data-social-module=\"socialBar\"></div>";
        const exp = newContainer.children[0] as HTMLDivElement;
        exp.setAttribute("data-social-id", id);
        exp.setAttribute("data-social-market", market);
        const separator = container.getElementsByClassName("share-container")[0] || container.getElementsByClassName("btns-container")[0] || container.getElementsByClassName("video-description")[0];
        if (separator) {
            container.insertBefore(newContainer, separator);
        } else {
            container.appendChild(newContainer);
        }
    } catch (e) {
        // no-op
    }
}

// Add comment container into view page (body)
function addOverlayCommentContainer(id, market) {
    const commentContainer = document.createElement("div");
    commentContainer.className = "microsoftNewsCommentModule";
    commentContainer.innerHTML = "<div id=\"social-experience-comment\" data-social-module=\"comment\"></div>";
    const exp = commentContainer.children[0] as HTMLDivElement;
    exp.setAttribute("data-social-id", id);
    exp.setAttribute("data-social-market", market);
    document.body.appendChild(commentContainer);
}

// Add social popup container into view page (body)
function addSocialPopupContainer(id, market) {
    const popupContainer = document.createElement("div");
    popupContainer.innerHTML = "<div id=\"social-experience-popup\" data-social-module=\"social\"></div>";
    const exp = popupContainer.children[0] as HTMLDivElement;
    exp.setAttribute("data-social-id", id);
    exp.setAttribute("data-social-market", market);
    document.body.appendChild(popupContainer);
}

// Create social bar, comments and social popup containers inside article, gallery, video pages
// Add publisher subscribe/follow button container
function addPublisherSubscribeFollowButtonContainer(id, market) {
    const buttonContainer = document.createElement("div");
    buttonContainer.style.display = "inline-block";
    buttonContainer.style.position = "relative";
    buttonContainer.style.bottom = "3.9rem";
    buttonContainer.style.left = "1.2rem";
    buttonContainer.innerHTML = "<div id=\"social-experience-publisher-subscribe-follow-button\" data-social-module=\"publisherSubscribeFollowButton\"></div>";

    const exp = buttonContainer.children[0] as HTMLDivElement;
    exp.setAttribute("data-social-id", id);
    exp.setAttribute("data-social-market", market);

    const header = document.getElementsByClassName("collection-headline-flex")[0];
    const publisherLogo = header.getElementsByClassName("partnerlogo-img")[0];
    header.insertBefore(buttonContainer, publisherLogo.nextSibling);
}

// Add gradient overlay container
function addGradientOverlayContainer(id, market) {
    const gradientOverlayContainer = document.createElement("div");
    gradientOverlayContainer.innerHTML = "<div id=\"social-experience-gradient-overlay\" data-social-module=\"gradientOverlay\"></div>";
    const exp = gradientOverlayContainer.children[0] as HTMLDivElement;
    exp.setAttribute("data-social-id", id);
    exp.setAttribute("data-social-market", market);
    document.body.appendChild(gradientOverlayContainer);
}

// Create social bar and comments containers inside article, gallery, video pages
function createContainers() {
    const pageConfig = getPageType();
    if (!pageConfig) {
        return;
    }
    if (pageConfig.pageType === "video") {
        addSocialBarIntoVideo(pageConfig.id, pageConfig.market);
    } else {
        addSocialBarIntoArticle(pageConfig.id, pageConfig.market);
    }
    if (!getFlight("prg-rctns_v0")) {
        addOverlayCommentContainer(pageConfig.id, pageConfig.market);
    }
    if (getFlight("prg-subs")) {
        addPublisherSubscribeFollowButtonContainer(pageConfig.id, pageConfig.market);
        addGradientOverlayContainer(pageConfig.id, pageConfig.market);
    }
    addSocialPopupContainer(pageConfig.id, pageConfig.market);
}

function renderSocialBar(app, root, designSystem, requestContext) {
    const context = {
        handleTelemetryCall,
        handlePartialPageImpressionCall,
        handleAppErrorEventCall,
        id: root.dataset.socialId,
        market: root.dataset.socialMarket
    };

    const props: ExperienceInitialProps<any> = {
        parentTelemetry: {},
        context
    };
    const socialBarConnector = new SocialBarConnector("SocialBar", "", app.rootReducer, new SocialBarReducer(), app.store, {...experienceConfigSocialBar.properties, dataConnectors: []}, requestContext);
    socialBarConnector.connectComponent(SocialBarToolingInfo.reactComponent, props).then((socialExperience) => {
        app.renderRootElement(
            React.createElement(
                DesignSystemProvider,
                { designSystem },
                React.createElement(socialExperience, {...props.context})
            ),
            root);
    });
}

function renderSocialComment(app, root, designSystem, requestContext, commentDisplayType) {
    const lowerCaseHref = document.location.href.toLowerCase();
    const specificCommentId = lowerCaseHref.indexOf("commentid=") != -1 && lowerCaseHref.split("commentid=").pop().split("&")[0];
    const context = {
        handleTelemetryCall,
        handlePartialPageImpressionCall,
        handleAppErrorEventCall,
        id: root.dataset.socialId,
        market: root.dataset.socialMarket,
        commentDisplayType
    };
    if (specificCommentId && specificCommentId != "undefined") {
        context["specificCommentId"] = specificCommentId;
    }

    const props: ExperienceInitialProps<any> = {
        parentTelemetry: {},
        context
    };
    const socialCommentConnector = new SocialCommentConnector("SocialComment", "", app.rootReducer, new SocialCommentReducer(), app.store, {...experienceConfigSocialComment.properties, dataConnectors: []}, requestContext);
    socialCommentConnector.connectComponent(SocialCommentToolingInfo.reactComponent, props).then((socialExperience) => {
        app.renderRootElement(
            React.createElement(
                DesignSystemProvider,
                { designSystem },
                React.createElement(socialExperience, {...props.context})
            ),
            root);
    });
}

function renderSocial(app, root, designSystem, requestContext, commentDisplayType, publisherSubscribeFollowButtonDataPromise, gradientOverlayDataPromise) {
    const context = {
        handleTelemetryCall,
        handlePartialPageImpressionCall,
        handleAppErrorEventCall,
        id: root.dataset.socialId,
        market: root.dataset.socialMarket,
        commentDisplayType,
        moduleName: root.dataset.socialModule
    };

    if (root.dataset.socialModule.toLowerCase() === "publishersubscribefollowbutton") {
        context["publisherSubscribeFollowButtonDataPromise"] = publisherSubscribeFollowButtonDataPromise.promise;
    } else if (root.dataset.socialModule.toLowerCase() === "gradientoverlay") {
        context["gradientOverlayDataPromise"] = gradientOverlayDataPromise.promise;
    }

    const props: ExperienceInitialProps<any> = {
        parentTelemetry: {},
        context
    };
    const socialConnector = new SocialConnector("Social", "", app.rootReducer, new SocialReducer(), app.store, {...experienceConfigSocial.properties, dataConnectors: []}, requestContext);
    socialConnector.connectComponent(SocialToolingInfo.reactComponent, props).then((socialExperience) => {
        app.renderRootElement(
            React.createElement(
                DesignSystemProvider,
                { designSystem },
                React.createElement(socialExperience, {...props.context})
            ),
            root);
    });
}

// Render experiences into existing containers
function renderToContainers(): void {
    if (!experienceConfigSocial || !experienceConfigSocialBar || !experienceConfigSocialComment) {
        return;
    }

    // Override the design system for dark mode and ltr/rtl
    const requestContext = new RequestContext();
    const ocid = UrlUtility.getQueryParameterByName("ocid", document.location && document.location.href);
    const isHybridWindowsPage = ocid && (ocid.toLowerCase().indexOf("winp") == 0 || ocid.toLowerCase().indexOf("win10newsapp") == 0);
    const isDarkModeSupported = isHybridWindowsPage || getFlight("darkmodevvs") || getFlight("prg-mobileappview");

    // Todo support other flight types in future
    const commentDisplayType = "overlay";

    // This flight requires widget to be shown under Ad container
    if (commentDisplayType == "overlay") {
        const newStyle = document.createElement("style");
        newStyle.innerHTML = `
            .videojsplayer .player-wrapper.shrunk {
                bottom: 125px;
                z-index: 2147483637;
            }
        `;
        document.body.appendChild(newStyle);
    }

    const darkModeQuery = matchMedia("(prefers-color-scheme:dark)");
    const backgroundColor = isDarkModeSupported && darkModeQuery.matches ? DarkModeBackgroundColor : DesignSystemDefaults.backgroundColor;
    const direction = document.dir && document.dir.toLowerCase() === "rtl" ? Direction.rtl : Direction.ltr;

    const designSystem = {
        ...DesignSystemDefaults,
        backgroundColor,
        direction
    };

    // Data promises for publisher subscribe/follow button and gradient overlay
    const createPromise = () => {
        let resolver;
        return {
            promise: new Promise(resolve => resolver = resolve),
            resolve: resolver
        };
    };
    const publisherSubscribeFollowButtonDataPromise = createPromise();
    const gradientOverlayDataPromise = createPromise();
    if (getFlight("prg-subs")) {
        const pageType = getPageType();
        const contentUrl = `https://assets.msn.com/content/view/v2/Detail/${pageType.market}/${pageType.id}`;
        sendRequest(() => fetch(contentUrl), "GetContentData")
            .then(async response => {
                const data = await response.json();
                publisherSubscribeFollowButtonDataPromise.resolve({
                    publisherId: data.provider?.profileId
                });
                gradientOverlayDataPromise.resolve({
                    publisherId: data.provider?.profileId,
                    publisherName: data.provider?.name,
                    publisherLogoUrl: data.provider?.logo?.url,
                    subscriptionProductType: data.subscriptionProductType
                });
            })
            .catch(() => { /* no-op */ });
    }

    // For each detected container, render corresponding experiences based on container definition.
    for (const key in roots) {
        const root = roots[key];
        // Root doesn't satisfy rendering conditions
        if (!root || !root.dataset || !root.dataset.socialId || !root.dataset.socialModule) {
            continue;
        }

        // We need a new store for each experience to isolate their redux per container.
        let app = null;

        // sticky and fixed roots are sharing the same redux store.
        if (key == "sticky" || key == "fixed") {
            app = apps["sticky"] || apps["fixed"];
        }

        if (!app) {
            PageBase.resetPageBaseInstance();
            app = PageBase.getInstance();
            app.start();
            apps[key] = app;
        }

        // Update app environment
        appEnvironment.HostPage = {
            originalUrl: location.href,
            hostName: location.hostname,
            topDomain: appEnvironment.HostPage.topDomain,
            verticalKey: root.dataset.verticalkey || (appEnvironment.HostPage && appEnvironment.HostPage.verticalKey) || "news",
            categoryKey: root.dataset.categorykey || (appEnvironment.HostPage && appEnvironment.HostPage.categoryKey) || "other"
        };

        // Create an instance of the social data connector
        const socialDataConnector = new SocialDataConnector(
            ConnectorNamespaces.SocialData,
            "",
            app.rootReducer,
            new SocialDataReducer(),
            app.store,
            undefined,
            requestContext
        );

        if (root.dataset.socialModule.toLowerCase() === "socialbar") {
            renderSocialBar(app, root, designSystem, requestContext);
        } else if (root.dataset.socialModule.toLowerCase() === "comment") {
            renderSocialComment(app, root, designSystem, requestContext, commentDisplayType);
        } else {
            renderSocial(app, root, designSystem, requestContext, commentDisplayType, publisherSubscribeFollowButtonDataPromise, gradientOverlayDataPromise);
        }
    }
}

function findRoots(setupContainers: boolean, removeOldContainers?: boolean): void {
    const placeHolders = {
        sticky: "social-experience-socialbar-sticky",
        fixed: "social-experience-socialbar-fixed",
        comment: "social-experience-comment",
        popup: "social-experience-popup",
        publisherSubscribeFollowButton: "social-experience-publisher-subscribe-follow-button",
        gradientOverlay: "social-experience-gradient-overlay",
        spaTest: "social"
    };
    const keys = Object.keys(placeHolders);
    const mockContentId = "BBBMTFk";

    keys.map(key => {
        try {
            // When Prime view contain embedded content we need to handle multiple containers with the same id
            if (document.querySelectorAll) {
                const placeholders = document.querySelectorAll(`[id=${placeHolders[key]}]`);
                if (placeholders.length > 0) {
                    roots[key] = placeholders[placeholders.length - 1] as HTMLElement;
                }
            }
        } catch (e) {
            // Suppress the error
        }
        if (!roots[key]) {
            roots[key] = document.getElementById(placeHolders[key]);
        }

        // Remove old container if we want to re-draw new widget for new id
        if (removeOldContainers && roots[key]) {
            roots[key].parentElement.removeChild(roots[key]);
            delete roots[key];
        }
    });

    // Prepare SPA test mode
    if (!roots.comment && !roots.sticky && !roots.fixed && !roots.popup && roots.spaTest && location.hash.indexOf("debugviewonly") != -1) {
        roots.spaTest.innerHTML = `
        ${placeHolders.comment}: <div id="${placeHolders.comment}" data-social-id="${mockContentId}" data-social-module="comment" data-social-market="en-us"></div>
        ${placeHolders.sticky}: <div id="${placeHolders.sticky}" data-social-id="${mockContentId}" data-social-module="socialBar" data-social-market="en-us"></div>
        ${placeHolders.fixed}: <div id="${placeHolders.fixed}" data-social-id="${mockContentId}" data-social-module="socialBar" data-social-market="en-us"></div>
        ${placeHolders.popup}: <div id="${placeHolders.popup}" data-social-id="${mockContentId}" data-social-module="social" data-social-market="en-us"></div>
        `;
        keys.map(key => roots[key] = document.getElementById(placeHolders[key]));
    }
    delete roots.spaTest;

    if (setupContainers && !roots.comment && !roots.sticky && !roots.fixed && !roots.popup) {
        createContainers();
        findRoots(false);
    }
}

findRoots(true);

// Currently hard coded the experience config index.
Promise.all(
    [
        resolver.getConfig("cms/api/amp/experienceConfigIndex/BB1enwaG"),
        resolver.getConfig("cms/api/amp/experienceConfigIndex/BB1dqeiP"),
        resolver.getConfig("cms/api/amp/experienceConfigIndex/BB1d8Nnn")
    ]).then((values) => {
    experienceConfigSocial = values[0];
    experienceConfigSocialBar = values[1];
    experienceConfigSocialComment = values[2];
    renderToContainers();
});
// Listen to update events from Prime (Necessary as video page auto updates current displayed video)
Mediator.registerSocialActionsUpdatedEvent(async () => {
    findRoots(true, true);
    renderToContainers();
});